<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { UiButton, UiText } from '@shopexperts/shared';
import { authHandoffQuery, isMessageRedirect } from '@/v2Projects/directMessageDraft';
import RegisterForm, { type RegisterAuthResult } from './leads/RegisterForm.vue';

const emit = defineEmits<{
    (e: 'authenticated', data: RegisterAuthResult): void;
}>();

const router = useRouter();
const route = useRoute();
const prefillEmail = computed(() => typeof route.query.email === 'string' ? route.query.email : '');
const loginLocation = computed(() => ({ path: '/login', query: authHandoffQuery(registerFormRef.value?.email || prefillEmail.value, route.query.redirect) }));

const registerFormRef = useTemplateRef<InstanceType<typeof RegisterForm>>('registerFormRef');

const isLoading = computed(() => registerFormRef.value?.isLoading ?? false);

const websiteUrl = (import.meta.env.VITE_WEBSITE_URL as string | undefined)?.replace(/\/$/, '') || '';
const termsClientHref = `${websiteUrl}/policies/terms-client`;
const privacyHref = `${websiteUrl}/policies/privacy`;

function handleSignUp() {
    registerFormRef.value?.triggerSubmit();
}

function handleAuthenticated(data: RegisterAuthResult) {
    emit('authenticated', data);
}

function goToLogin(email?: string) {
    router.push({ path: '/login', query: authHandoffQuery(email || prefillEmail.value, route.query.redirect) });
}
</script>

<template>
    <div class="flex flex-col gap-8">
        <!-- Header: Already have an account? Login -->
        <div class="flex flex-col gap-4">
            <UiText as="p" variant="p">
                <span>Already have an account? </span>
                <RouterLink :to="loginLocation" class="hover:underline text-se-highlight">
                    <UiText as="span" variant="p">
                        <span>Login</span>
                    </UiText>
                </RouterLink>
            </UiText>

            <!-- Title -->
             <UiText as="h1" variant="h3" text="Open a Free **Client Account**" />

            <!-- Subtitle -->
            <UiText as="p" variant="p" class="text-se-gray-600">
                <span>{{ isMessageRedirect(route.query.redirect) ? "Create your free account to continue to the conversation. Your message is saved and ready to review." : "Connect with Shopify experts around the world." }}</span>
            </UiText>
        </div>

        <!-- Register Form -->
        <RegisterForm
            ref="registerFormRef"
            :email="prefillEmail"
            @authenticated="handleAuthenticated"
            @switch-to-login="goToLogin"
        />

        <!-- Sign up button and terms -->
        <div class="flex flex-col gap-4">
            <UiButton
                type="button"
                variant="primary"
                label="Sign up"
                class="w-full"
                :disabled="isLoading"
                @click="handleSignUp"
            />

            <UiText as="p" variant="caption-normal" class="text-center text-se-gray-600">
                <span>By proceeding, you agree to the </span>
                <a :href="termsClientHref" class="hover:underline text-se-highlight" target="_blank" rel="noopener noreferrer">
                    <UiText as="span" variant="caption-normal">Terms &amp; Conditions</UiText>
                </a>
                <span> and </span>
                <a :href="privacyHref" class="hover:underline text-se-highlight" target="_blank" rel="noopener noreferrer">
                    <UiText as="span" variant="caption-normal">Privacy Policy</UiText>
                </a>
                <span>.</span>
            </UiText>
        </div>
    </div>
</template>

